import { useEffect, useState } from 'react';

import { css } from '@emotion/css';
import { Col, Row } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { DateConstant } from 'src/constants';
import { usePositionListSelector } from 'src/hooks';
import { LinkText, MButton, MCard, MTable, MTypography, CurrencyField, PercentageField } from 'src/lib';
import { Position, PositionTreeView } from 'src/models';
import { ColorConstant, Spacing } from 'src/styles';

import { SecurityHoldingNewTradeModal } from './SecurityHoldingNewTradeModal';
import { TradeSecurityHoldingModal } from './SecurityHoldingTradeModal';

const tradeButtonStyle = css`
  min-width: 5rem !important;
`;

export const SecurityHoldingsTable = () => {
  const [selectedSecurity, setSelectedSecurity] = useState<Position | PositionTreeView | null>(null);
  const [isTradeSecurityModalOpen, setIsTradeSecurityModalOpen] = useState(false);
  const [isNewTradeSecurityModalOpen, setIsNewTradeSecurityModalOpen] = useState(false);
  const [groupedPositionList, setGroupedPositionList] = useState<PositionTreeView[]>([]);

  const { tradablePositionList, isPositionListLoading } = usePositionListSelector();

  const cols: ColumnsType<PositionTreeView> = [
    {
      title: 'Name',
      dataIndex: 'description',
      key: 'description',
      render: value => <LinkText value={value} />,
      width: '15%',
    },
    {
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'Last Price',
      dataIndex: 'value',
      render: (value: number) => <CurrencyField value={value} />,
    },
    {
      title: 'Current Value',
      dataIndex: 'totalValue',
      render: (totalValue: number) => <CurrencyField value={totalValue} />,
    },
    {
      title: '% Change',
      dataIndex: 'change',
      render: (change: number) => (
        <PercentageField value={change} color={change >= 0 ? ColorConstant.GREEN6 : ColorConstant.RED6} />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (value: PositionTreeView | Position) => (
        <MButton
          type='primary'
          onClick={() => {
            onRowClick(value);
          }}
          className={tradeButtonStyle}>
          Trade
        </MButton>
      ),
      width: 125,
    },
  ];

  const expandableCols: ColumnsType<Position> = [
    {
      key: 'expandPlaceholder',
      width: '50px',
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: value => moment(value).format(DateConstant.US_FORMAT),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'Avg Price',
      dataIndex: 'purchasePrice',
      render: (purchasePrice: number) => <CurrencyField value={purchasePrice} />,
    },
    {
      title: 'Cost',
      dataIndex: 'totalCost',
      render: (totalCost: number) => <CurrencyField value={totalCost} />,
    },
    {
      title: 'Last Price',
      dataIndex: 'value',
      render: (value: number) => <CurrencyField value={value} />,
    },
    {
      title: 'Current Value',
      dataIndex: 'totalValue',
      render: (totalValue: number) => <CurrencyField value={totalValue} />,
    },
    {
      title: '% Change',
      dataIndex: 'change',
      render: (change: number) => (
        <PercentageField value={change} color={change >= 0 ? ColorConstant.GREEN6 : ColorConstant.RED6} />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: value => (
        <MButton type='primary' onClick={() => onRowClick(value)} className={tradeButtonStyle}>
          Trade
        </MButton>
      ),
      width: 125,
    },
  ];

  const onCloseTradeSecurityModal = () => {
    setIsTradeSecurityModalOpen(false);
    setSelectedSecurity(null);
  };

  const onRowClick = (record: PositionTreeView | Position) => {
    setSelectedSecurity(record);
    setIsTradeSecurityModalOpen(true);
  };

  const expandableRender = (position: PositionTreeView) => {
    return <MTable columns={expandableCols} data={position.children} rowKey={record => record.id} pagination={false} />;
  };

  useEffect(() => {
    const groupedPositions = tradablePositionList.reduce((acc: PositionTreeView[], position) => {
      const existingPosition = acc.find(p => p.symbol === position.symbol);

      if (existingPosition) {
        existingPosition.quantity += position.quantity;
        existingPosition.totalCost += position.totalCost;
        existingPosition.totalValue = existingPosition.quantity * (position.value ?? 0);
        existingPosition.change = position.change;

        existingPosition.children.push(position);

        return acc;
      }

      acc.push({ ...position, children: [position] });

      return acc;
    }, []);
    setGroupedPositionList(groupedPositions);
  }, [tradablePositionList]);

  return (
    <Row>
      <Col span={24} id='securities-section'>
        <Row>
          <Col span={24} className={Spacing.my16}>
            <Row justify='space-between' align='middle' className={Spacing.mb12}>
              <MTypography variant='h4' weight='semibold' color={ColorConstant.BRAND6}>
                Securities Holdings
              </MTypography>
              <MButton type='primary' onClick={() => setIsNewTradeSecurityModalOpen(true)} testId={'new-trade-btn'}>
                New Trade
              </MButton>
            </Row>
            <Row>
              <Col span={24} className={Spacing.my4}>
                <MTypography variant='subtitle1' inline>
                  Want to trade? Click the{' '}
                </MTypography>
                <MTypography variant='subtitle1' weight='semibold' inline>
                  &quot;New Trade&quot;
                </MTypography>{' '}
                <MTypography variant='subtitle1' inline>
                  button on the right to trade a new security, or use the{' '}
                </MTypography>
                <MTypography variant='subtitle1' weight='semibold' inline>
                  &quot;Trade&quot;
                </MTypography>{' '}
                <MTypography variant='subtitle1' inline>
                  button next to a security below to buy or sell it.
                </MTypography>
              </Col>
              <Col span={24} className={Spacing.my4}>
                <MTypography variant='subtitle2'>
                  Intraday quotes are delayed up to 20 minutes, and do not reflect current bid, ask or last sale.
                </MTypography>
              </Col>
            </Row>
          </Col>
          <Col span={24} className={Spacing.my16}>
            <MCard>
              <MTable<PositionTreeView>
                columns={cols}
                data={groupedPositionList}
                pagination={false}
                loading={isPositionListLoading}
                scroll={{ y: 750 }}
                expandable={{
                  expandedRowRender: record => expandableRender(record),
                  childrenColumnName: 'none', // Disable automatic tree nesting
                  columnWidth: '50px',
                }}
                rowKey={record => record.id}
                testId={'securities-holdings-table'}
              />
            </MCard>
          </Col>
        </Row>
      </Col>
      {selectedSecurity && (
        <TradeSecurityHoldingModal
          open={isTradeSecurityModalOpen}
          onClose={onCloseTradeSecurityModal}
          position={selectedSecurity}
        />
      )}
      <SecurityHoldingNewTradeModal
        open={isNewTradeSecurityModalOpen}
        onClose={() => setIsNewTradeSecurityModalOpen(false)}
      />
    </Row>
  );
};
